import * as React from "react";
import { useForm, useFieldArray, useWatch, Control, UseFieldArrayReturn } from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  ActionIcon,
  Group,
  ScrollArea,
  Stack,
  Flex,
  useMantineTheme,
  Divider,
  Modal,
  Text,
} from "@mantine/core";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { IconChevronLeft, IconChevronRight, IconEye, IconPlus, IconTrash } from "@tabler/icons-react";

import { DatasetSchema, DatasetType } from "@supportinitiative/common";

import { CSelect, CTextInput, CCheckbox, CMultiSelect, CTextarea } from "@supportinitiative/react-hook-form-mantine";

import { styles } from "../styles";
import { postDataset } from "../api";
import { AuditType, IDispatchAction } from "../types";
import {
  getColumnsLOV,
  getActionOptions,
  columnTypes,
  // DatasetSchema,
} from "./DatasetUtils";
import { UserContext } from "../misc/UserContext";
import { useDataset } from "../hooks";
import FormSubmitError from "../ui/Error/FormSubmitError";
import FormStateError from "../ui/Error/FormStateError";
import Success from "../ui/Success";
import Audit from "../ui/Audit/Audit";
import { getId } from "../utilities";

type actionTypes = "CURRENT-ACTION" | "ACTION-OPTIONS" | "SET-VALUE" | "SET-SUBMITTING" | "SET-SUCCESS" | "SET-ERROR";

const NO_OF_COLUMNS = 10;

function SetColumns({ control }: { control: Control<DatasetType> }) {
  const columns = useWatch({
    control,
    name: "definition.columns",
  });
  const { state: datasetFormState } = React.useContext(DatasetCVMContext);

  return (
    <Stack gap="0" mb="lg">
      <Group align="center" style={{ alignItems: "center" }}>
        <Flex>
          <Title
            mb="xs"
            mt="xs"
            order={5}
            style={{
              padding: "0px 5px",
              backgroundColor: "#16A596",
              color: "#fff",
            }}
          >
            Set Columns
          </Title>
        </Flex>
      </Group>

      <Grid align="stretch" gutter={"xs"} mb="lg">
        <Grid.Col span={8}>
          <CMultiSelect
            name={"definition.setColumns"}
            control={control}
            searchable
            data={getColumnsLOV(columns)}
            disabled={datasetFormState.actionOptions.disableAll}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

function PrimaryKey({ control }: { control: Control<DatasetType> }) {
  const columns = useWatch({
    control,
    name: "definition.columns",
  });
  const { state: datasetFormState } = React.useContext(DatasetCVMContext);

  return (
    <Stack gap="0" mb="lg">
      <Group align="center" style={{ alignItems: "center" }}>
        <Flex>
          <Title
            mb="xs"
            mt="xs"
            order={5}
            style={{
              padding: "0px 5px",
              backgroundColor: "#16A596",
              color: "#fff",
            }}
          >
            Primary Key
          </Title>
        </Flex>
      </Group>

      <Grid align="stretch" gutter={"xs"} mb="lg">
        <Grid.Col span={8}>
          <CMultiSelect
            name={"definition.primaryKey"}
            control={control}
            searchable
            data={getColumnsLOV(columns)}
            disabled={datasetFormState.actionOptions.disableAll}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

function ColumnToTrackDataLoad({ control }: { control: Control<DatasetType> }) {
  const columns = useWatch({
    control,
    name: "definition.columns",
  });
  const { state: datasetFormState } = React.useContext(DatasetCVMContext);

  return (
    <Stack gap="0" mb="lg">
      <Group align="center" style={{ alignItems: "center" }}>
        <Flex>
          <Title
            mb="xs"
            mt="xs"
            order={5}
            style={{
              padding: "0px 5px",
              backgroundColor: "#16A596",
              color: "#fff",
            }}
          >
            Column to Track
          </Title>
        </Flex>
      </Group>
      <Grid align="stretch" gutter={"xs"} mb="lg">
        <Grid.Col span={8}>
          <CSelect
            name={`definition.columnToTrackDataLoad`}
            control={control}
            clearable
            searchable
            data={getColumnsLOV(columns)}
            disabled={datasetFormState.actionOptions.disableAll}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

function Uniques({ control }: { control: Control<DatasetType> }) {
  const columns = useWatch({
    control,
    name: "definition.columns",
  });

  const uniquesArray = useFieldArray({
    control,
    name: "definition.uniques",
  });
  const { state: datasetFormState } = React.useContext(DatasetCVMContext);

  return (
    <Stack gap="0" mb="lg">
      <Group align="center" style={{ alignItems: "center" }}>
        <Flex>
          <Title
            mb="xs"
            mt="xs"
            order={5}
            style={{
              padding: "0px 5px",
              backgroundColor: "#16A596",
              color: "#fff",
            }}
          >
            Uniques
          </Title>
        </Flex>
        <ActionIcon
          size="sm"
          variant="transparent"
          color="gray"
          onClick={() => {
            uniquesArray.append({
              unique: [],
            });
          }}
          style={{
            display: datasetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
          }}
          disabled={datasetFormState.actionOptions.disableAll}
        >
          <IconPlus />
        </ActionIcon>
      </Group>

      {uniquesArray.fields.length === 0 ? (
        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={8}>
            <Divider label="No Uniques" labelPosition="left" />
          </Grid.Col>
        </Grid>
      ) : (
        <Stack gap="0" mb="lg">
          {uniquesArray.fields.map((field, index) => {
            return (
              <Grid align="stretch" gutter={"xs"} mb="xs" key={field.id}>
                <Grid.Col span={7}>
                  <CMultiSelect
                    name={`definition.uniques.${index}.unique`}
                    control={control}
                    searchable
                    data={getColumnsLOV(columns)}
                    disabled={datasetFormState.actionOptions.disableAll}
                  />
                </Grid.Col>

                <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    color="gray"
                    onClick={() => {
                      uniquesArray.remove(index);
                    }}
                    style={{
                      display: datasetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
                    }}
                    disabled={datasetFormState.actionOptions.disableAll}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

function Indexes({ control }: { control: Control<DatasetType> }) {
  const columns = useWatch({
    control,
    name: "definition.columns",
  });

  const indexesArray = useFieldArray({
    control,
    name: "definition.indexes",
  });
  const { state: datasetFormState } = React.useContext(DatasetCVMContext);

  return (
    <Stack gap="0" mb="lg">
      <Group align="center" style={{ alignItems: "center" }}>
        <Flex>
          <Title
            mb="xs"
            mt="xs"
            order={5}
            style={{
              padding: "0px 5px",
              backgroundColor: "#16A596",
              color: "#fff",
            }}
          >
            Indexes
          </Title>
        </Flex>
        <ActionIcon
          size="sm"
          variant="transparent"
          color="gray"
          onClick={() => {
            indexesArray.append({
              index: [],
            });
          }}
          style={{
            display: datasetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
          }}
          disabled={datasetFormState.actionOptions.disableAll}
        >
          <IconPlus />
        </ActionIcon>
      </Group>

      {indexesArray.fields.length === 0 ? (
        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={8}>
            <Divider label="No Indexes" labelPosition="left" />
          </Grid.Col>
        </Grid>
      ) : (
        <Stack gap="0" mb="lg">
          {indexesArray.fields.map((field, index) => {
            return (
              <Grid align="stretch" gutter={"xs"} mb="xs" key={field.id}>
                <Grid.Col span={7}>
                  <CMultiSelect
                    name={`definition.indexes.${index}.index`}
                    control={control}
                    searchable
                    data={getColumnsLOV(columns)}
                    disabled={datasetFormState.actionOptions.disableAll}
                  />
                </Grid.Col>

                <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    color="gray"
                    onClick={() => {
                      indexesArray.remove(index);
                    }}
                    style={{
                      display: datasetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
                    }}
                    disabled={datasetFormState.actionOptions.disableAll}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

function Column({
  columnsArray,
  startColumn,
  index,
  control,
}: {
  columnsArray: UseFieldArrayReturn<DatasetType, "definition.columns", "__key">;
  startColumn: number;
  index: number;
  control: Control<DatasetType>;
}) {
  const { state: datasetFormState } = React.useContext(DatasetCVMContext);

  const codeValuesArray = useFieldArray({
    control,
    name: `definition.columns.${startColumn + index}.codeValues`,
  });

  const [showDetails, setShowDetails] = React.useState(false);

  return (
    <>
      <Grid align="stretch" gutter={"xs"} mb="xs">
        <Grid.Col span={2}>
          <CTextInput
            name={`definition.columns.${startColumn + index}.name`}
            control={control}
            showErrorText={false}
            label={index === 0 ? "Name" : ""}
            required
            disabled={datasetFormState.actionOptions.disableAll}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <CSelect
            name={`definition.columns.${startColumn + index}.type`}
            control={control}
            label={index === 0 ? "Type" : ""}
            data={columnTypes}
            showErrorText={false}
            required
            disabled={datasetFormState.actionOptions.disableAll}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <CTextInput
            name={`definition.columns.${startColumn + index}.label`}
            control={control}
            showErrorText={false}
            label={index === 0 ? "Label" : ""}
            disabled={datasetFormState.actionOptions.disableAll}
          />
        </Grid.Col>

        <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
          <Button
            variant="default"
            fullWidth
            onClick={() => {
              setShowDetails(true);
            }}
          >
            Details
          </Button>
        </Grid.Col>

        <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
          <ActionIcon
            size="sm"
            variant="transparent"
            color="gray"
            onClick={() => {
              columnsArray.remove(startColumn + index);
            }}
            style={{
              display: datasetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
            }}
            disabled={datasetFormState.actionOptions.disableAll}
          >
            <IconTrash />
          </ActionIcon>
        </Grid.Col>
      </Grid>

      <Modal
        centered
        size={"xl"}
        opened={showDetails}
        onClose={() => setShowDetails(!showDetails)}
        title={<Title order={3}>Properties</Title>}
      >
        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={12}>
            <CTextInput
              name={`definition.columns.${startColumn + index}.id`}
              control={control}
              label={"ID"}
              disabled={true}
            />
          </Grid.Col>
        </Grid>
        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={12}>
            <CTextarea
              name={`definition.columns.${startColumn + index}.comment`}
              control={control}
              showErrorText={false}
              autosize
              minRows={2}
              maxRows={20}
              label={"Comment"}
              disabled={datasetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={12}>
            <CTextarea
              name={`definition.columns.${startColumn + index}.genFunction`}
              control={control}
              showErrorText={false}
              autosize
              minRows={2}
              maxRows={20}
              label={"Generate Function"}
              disabled={datasetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={6}>
            <CTextInput
              name={`definition.columns.${startColumn + index}.default`}
              control={control}
              showErrorText={false}
              label={"Default"}
              disabled={datasetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CTextInput
              name={`definition.columns.${startColumn + index}.format`}
              control={control}
              showErrorText={false}
              label={"Format"}
              disabled={datasetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Divider mt="lg" label="Constraints" labelPosition="center" />

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={2} style={{ display: "flex", alignItems: "flex-end" }}>
            <CCheckbox
              name={`definition.columns.${startColumn + index}.notNull`}
              control={control}
              label="Not Null"
              trueValue="YES"
              falseValue="NO"
              disabled={datasetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Divider mt="lg" label="Display Component" labelPosition="center" />

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={6}>
            <CSelect
              name={`definition.columns.${startColumn + index}.component`}
              control={control}
              showErrorText={false}
              data={[
                { label: "Text", value: "TEXT" },
                { label: "Badge", value: "BADGE" },
                { label: "Date", value: "DATE" },
              ]}
              label={"Component"}
              disabled={datasetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CTextarea
              name={`definition.columns.${startColumn + index}.componentProps`}
              control={control}
              showErrorText={false}
              autosize
              minRows={2}
              maxRows={20}
              label={"Props"}
              disabled={datasetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Divider mt="lg" label="Code Values" labelPosition="center" />

        <Flex>
          <Group mb="xs" style={{ alignItems: "center" }}>
            <Text fw={500} style={{ fontSize: "14px" }}>
              Values
            </Text>
            <ActionIcon
              size="sm"
              variant="transparent"
              color="gray"
              onClick={() => {
                codeValuesArray.append({
                  name: "",
                  value: "",
                });
              }}
              disabled={datasetFormState.actionOptions.disableAll}
            >
              <IconPlus />
            </ActionIcon>
          </Group>
        </Flex>

        <Stack gap="0" mb="lg">
          {codeValuesArray.fields.map((field, codeIndex) => {
            return (
              <Grid align="stretch" gutter={"xs"} mb="xs" key={field.id}>
                <Grid.Col span={5}>
                  <CTextInput
                    name={`definition.columns.${startColumn + index}.codeValues.${codeIndex}.name`}
                    control={control}
                    disabled={datasetFormState.actionOptions.disableAll}
                    label={codeIndex === 0 ? "Name" : ""}
                  />
                </Grid.Col>
                <Grid.Col span={5}>
                  <CTextInput
                    name={`definition.columns.${startColumn + index}.codeValues.${codeIndex}.value`}
                    control={control}
                    disabled={datasetFormState.actionOptions.disableAll}
                    label={codeIndex === 0 ? "Value" : ""}
                  />
                </Grid.Col>
                <Grid.Col span={2} style={{ display: "flex", alignItems: "flex-end" }}>
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    color="gray"
                    onClick={() => {
                      codeValuesArray.remove(index);
                    }}
                    style={{
                      display: datasetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
                    }}
                    disabled={datasetFormState.actionOptions.disableAll}
                  >
                    <IconTrash />
                  </ActionIcon>
                </Grid.Col>
              </Grid>
            );
          })}
        </Stack>

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={2}>
            <Button
              color="teal"
              variant="filled"
              fullWidth
              type="submit"
              onClick={
                () => {
                  setShowDetails(false);
                } // Close the modal on Ok
              }
            >
              Ok
            </Button>
          </Grid.Col>
        </Grid>
      </Modal>
    </>
  );
}

export const DatasetCVMContext = React.createContext<{
  state: any;
  dispatch: React.Dispatch<IDispatchAction<actionTypes>>;
}>({
  state: {},
  dispatch: () => {},
});

const initialState = {
  currentAction: "NEW",
  actionOptions: {},
  setValue: () => {},
  submitting: false,
  error: "",
  successMessage: "",
};

function reducer(state: any, action: IDispatchAction<actionTypes>) {
  switch (action.type) {
    case "CURRENT-ACTION":
      return { ...state, currentAction: action.value };
    case "ACTION-OPTIONS":
      return { ...state, actionOptions: action.value };
    case "SET-VALUE":
      return { ...state, setValue: action.value };
    case "SET-SUBMITTING":
      return { ...state, submitting: action.value };
    case "SET-SUCCESS":
      return { ...state, successMessage: action.value };
    case "SET-ERROR":
      return { ...state, error: action.value };
    default:
      return state;
  }
}

export default function DatasetCVM() {
  // let [dataset, setDataset] = React.useState<DatasetType>();

  let [audit, setAudit] = React.useState<AuditType | undefined>();

  // let [currentAction, setCurrentAction] = React.useState("new");
  let navigate = useNavigate();
  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId");
  const { state: applicationState } = React.useContext(UserContext);
  const queryClient = useQueryClient();
  const theme = useMantineTheme();

  const { data: dataset } = useDataset(entityId, id);
  // let [newDataset, setNewDataset] = React.useState<DatasetType | undefined>();
  const [datasetFormState, datasetFormDispatch] = React.useReducer(reducer, {
    ...initialState,
    actionOptions: getActionOptions("new", dataset, dataset, applicationState),
  });
  // console.log("dataset in component", dataset);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<DatasetType>({
    defaultValues: {
      name: "",
      displayName: "",
      description: "",
      definition: {
        primaryKey: [],
        columns: [],
        indexes: [],
        uniques: [],
        columnToTrackDataLoad: "",
        allowMerge: "NO",
        maintainHistory: "NO",
      },
    },
    criteriaMode: "all",
    resolver: yupResolver(DatasetSchema),
  });

  const columnsArray = useFieldArray({
    control,
    name: "definition.columns",
    keyName: "__key",
  });
  /* 
  const columns = useWatch({
    control,
    name: "definition.columns",
  }); */

  // const [columns, setColumns] = React.useState<DatasetType["definition"]["columns"]>([]);

  const [startColumn, setStartColumn] = React.useState(0);
  const [endColumn, setEndColumn] = React.useState(NO_OF_COLUMNS);
  const onOk = () => {
    navigate(`/app/datasets?entityId=${entityId}`);
  };

  React.useEffect(() => {
    if (dataset) {
      datasetFormDispatch({ type: "CURRENT-ACTION", value: "VIEW" });
      datasetFormDispatch({
        type: "ACTION-OPTIONS",
        value: getActionOptions("view", dataset, dataset, applicationState),
      });

      // setColumns(dataset.definition.columns || []);

      setAudit({
        status: dataset.status,
        authStatus: dataset.authStatus,
        usableStatus: dataset.usableStatus,
        makerId: dataset.makerId,
        makerDate: dataset.makerDate,
        modificationNo: dataset.modificationNo,
        checkerId: dataset.checkerId,
        checkerDate: dataset.checkerDate,
      });

      reset(dataset);
    }
  }, [dataset]);

  const onSubmit = async (data: any) => {
    try {
      datasetFormDispatch({
        type: "SET-SUBMITTING",
        value: true,
      });
      datasetFormDispatch({
        type: "SET-ERROR",
        value: "",
      });
      let payload = {
        ...data,
        entityId,
        metaData: {
          action: datasetFormState.currentAction,
        },
      };
      await postDataset(payload);
      // setError("Form", "Unable to Submit");
      datasetFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      queryClient.invalidateQueries(["dataset", entityId, id]);
      queryClient.invalidateQueries(["datasets", entityId]);
      datasetFormDispatch({
        type: "SET-SUCCESS",
        value: "Dataset Saved Successfully",
      });
    } catch (error: any) {
      // console.log("post catch", error);
      // console.log("post catch", error?.response?.data?.message);
      datasetFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      datasetFormDispatch({
        type: "SET-ERROR",
        value: error?.response?.data?.message || "Unable to Save Dataset",
      });
    }
  };

  return (
    <DatasetCVMContext.Provider value={{ state: datasetFormState, dispatch: datasetFormDispatch }}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: theme.spacing.lg }} noValidate>
        <div id="header">
          <Grid
            align="flex-end"
            gutter={"xs"}
            mt="xs"
            mb="xs"
            // mb="xs"
            style={{
              display: "flex",
              // borderLeft: `${theme.spacing.xxs} solid ${theme.colors.emerald[7]}`,
            }}
            columns={12}
          >
            <Grid.Col span={3}>
              <Flex align={"end"} gap={theme.spacing.xs}>
                <Stack gap={0}>
                  <Title order={3} c="dark">
                    Dataset
                  </Title>
                </Stack>
              </Flex>
            </Grid.Col>
          </Grid>
        </div>

        <Divider size="sm" mb="xs" color="dark.2" />

        <div id="body">
          <Grid align="flex-start" gutter={"xs"} mb="xs">
            <Grid.Col span={2}>
              <CTextInput
                name="name"
                control={control}
                label="Name"
                aria-label="Name"
                required
                showErrorText={false}
                disabled={datasetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CTextInput
                name="displayName"
                control={control}
                label="Display Name"
                aria-label="Display Name"
                showErrorText={false}
                disabled={datasetFormState.actionOptions.disableAll}
              />
            </Grid.Col>

            <Grid.Col span={2}>
              <CSelect
                name="definition.type"
                control={control}
                label="Type"
                aria-label="Type"
                data={[
                  { label: "Pure Incremental", value: "PURE_INCREMENTAL" },
                  { label: "Set Incremental", value: "SET_INCREMENTAL" },
                  { label: "Set Full", value: "SET_FULL" },
                  { label: "Full", value: "FULL" },
                ]}
                required
                showErrorText={false}
                disabled={datasetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          <Grid align="flex-start" gutter={"xs"} mb="xs" style={{ display: "flex", alignItems: "flex-end" }}>
            <Grid.Col span={8}>
              <CTextarea
                name="description"
                control={control}
                label="Description"
                autosize
                minRows={2}
                maxRows={20}
                required
                showErrorText={false}
                disabled={datasetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          <Stack gap="0" mb="lg" mt="xs">
            <Group mb="xs" gap={"xs"} style={{ alignItems: "center" }}>
              <Flex>
                <Title
                  mb="xs"
                  mt="xs"
                  order={5}
                  style={{
                    padding: "0px 5px",
                    backgroundColor: "#16A596",
                    color: "#fff",
                  }}
                >
                  Columns
                </Title>
              </Flex>

              <ActionIcon
                size="sm"
                variant="transparent"
                color="gray"
                onClick={() => {
                  if (startColumn >= NO_OF_COLUMNS) {
                    setStartColumn(startColumn - NO_OF_COLUMNS);
                    setEndColumn(endColumn - NO_OF_COLUMNS);
                  }
                }}
                disabled={startColumn < NO_OF_COLUMNS}
              >
                <IconChevronLeft stroke={2} />
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="transparent"
                color="gray"
                onClick={() => {
                  if (endColumn < columnsArray.fields.length) {
                    setStartColumn(startColumn + NO_OF_COLUMNS);
                    setEndColumn(endColumn + NO_OF_COLUMNS);
                  }
                }}
                disabled={endColumn >= columnsArray.fields.length}
              >
                <IconChevronRight stroke={2} />
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="transparent"
                color="gray"
                onClick={() => {
                  columnsArray.append({
                    name: "",
                    type: "TEXT",
                    genFunction: "",
                    label: "",
                    notNull: "NO",
                    id: "",
                    default: "",
                    displayFormat: "",
                    inputFormat: "",
                    format: "",
                    comment: "",
                    component: "TEXT",
                    componentProps: "",
                    codeValues: [],
                  });
                }}
                style={{
                  display: datasetFormState.currentAction.toUpperCase() === "VIEW" ? "none" : "",
                }}
                disabled={datasetFormState.actionOptions.disableAll}
              >
                <IconPlus stroke={2} />
              </ActionIcon>
            </Group>

            <ScrollArea
              style={{
                height:
                  datasetFormState.currentAction.toUpperCase() === "EDIT" ||
                  datasetFormState.currentAction.toUpperCase() === "NEW"
                    ? Math.min(Math.max(columnsArray.fields.length || 3, 3) * 50, 250)
                    : Math.min((columnsArray.fields.length || 0) * 100, 250),
              }}
              type="auto"
              offsetScrollbars
              scrollbarSize={16}
            >
              {columnsArray.fields.slice(startColumn, endColumn).map((field, index) => {
                return (
                  <Column
                    columnsArray={columnsArray}
                    control={control}
                    key={field.__key}
                    startColumn={startColumn}
                    index={index}
                  />
                );
              })}
            </ScrollArea>
          </Stack>

          <SetColumns control={control} />

          <PrimaryKey control={control} />

          <Uniques control={control} />

          <Indexes control={control} />

          <ColumnToTrackDataLoad control={control} />

          <Stack gap="0" mb="lg">
            <Flex>
              <Title
                mb="xs"
                mt="xs"
                order={5}
                style={{
                  padding: "0px 5px",
                  backgroundColor: "#16A596",
                  color: "#fff",
                }}
              >
                Preferences
              </Title>
            </Flex>
            <Grid align="stretch" gutter={"xs"} mb="xs">
              <Grid.Col span={2}>
                <CCheckbox
                  name={"definition.allowMerge"}
                  control={control}
                  label="Allow merge"
                  trueValue="YES"
                  falseValue="NO"
                  disabled={datasetFormState.actionOptions.disableAll}
                />
              </Grid.Col>
              <Grid.Col span={2}>
                <CCheckbox
                  name={"definition.maintainHistory"}
                  control={control}
                  label="Maintain history"
                  trueValue="YES"
                  falseValue="NO"
                  disabled={datasetFormState.actionOptions.disableAll}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </div>

        {(Object.keys(errors).length > 0 || datasetFormState.error) && (
          <div style={styles.errors} id="errors">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormStateError errors={errors} />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormSubmitError error={datasetFormState.error} />
            </div>
          </div>
        )}

        <Grid align="stretch" gutter={"xs"} columns={24} mb={"lg"}>
          <>
            {(datasetFormState.actionOptions.allowedActions || []).map((action: string) => {
              if (action === "save") {
                return (
                  <Grid.Col span={3} key={action}>
                    <Button
                      fullWidth
                      type="submit"
                      color="teal"
                      aria-label="Save"
                      loading={datasetFormState.submitting}
                    >
                      Save
                    </Button>
                  </Grid.Col>
                );
              } else if (action === "cancel") {
                return (
                  <Grid.Col span={3} key={action}>
                    <Button
                      variant="default"
                      fullWidth
                      component={Link}
                      to={`/app/datasets?entityId=${entityId}`}
                      aria-label="Cancel"
                    >
                      Cancel
                    </Button>
                  </Grid.Col>
                );
              } else if (action === "back") {
                return (
                  <Grid.Col span={3} key={action}>
                    <Button
                      variant="default"
                      fullWidth
                      component={Link}
                      to={`/app/datasets?entityId=${entityId}`}
                      aria-label="Back"
                    >
                      Back
                    </Button>
                  </Grid.Col>
                );
              } else if (action === "edit") {
                return (
                  <Grid.Col span={3} key={action}>
                    <Button
                      variant="default"
                      aria-label="Edit"
                      fullWidth
                      onClick={() => {
                        datasetFormDispatch({
                          type: "CURRENT-ACTION",
                          value: "EDIT",
                        });
                        datasetFormDispatch({
                          type: "ACTION-OPTIONS",
                          value: getActionOptions("edit", dataset, dataset, applicationState),
                        });

                        setAudit({
                          status: dataset?.status,
                          authStatus: "UNAUTH",
                          usableStatus: dataset?.usableStatus,
                          makerId: applicationState.user.userId,
                          makerDate: new Date(),
                          modificationNo:
                            dataset?.authStatus === "UNAUTH"
                              ? dataset?.modificationNo
                              : (dataset?.modificationNo || 0) + 1,
                          checkerId: "",
                          checkerDate: undefined,
                        });
                      }}
                    >
                      Edit
                    </Button>
                  </Grid.Col>
                );
              } else if (action === "auth") {
                return (
                  <Grid.Col span={3} key={action}>
                    <Button
                      variant="default"
                      aria-label="Auth"
                      fullWidth
                      onClick={async () => {
                        datasetFormDispatch({
                          type: "CURRENT-ACTION",
                          value: "AUTH",
                        });

                        try {
                          let payload = {
                            ...dataset,
                            entityId,
                            metaData: {
                              action: "auth",
                            },
                          };
                          await postDataset(payload);
                          // setError("Form", "Unable to Submit");
                          datasetFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "VIEW",
                          });

                          datasetFormDispatch({
                            type: "SET-SUCCESS",
                            value: "Dataset Authorized Successfully",
                          });
                        } catch (error: any) {
                          // console.log("post catch", error);
                          datasetFormDispatch({
                            type: "SET-ERROR",
                            value: error?.response?.data?.message || "Unable to Authorize Dataset",
                          });
                        }
                      }}
                    >
                      Auth
                    </Button>
                  </Grid.Col>
                );
              } else if (action === "delete") {
                return (
                  <Grid.Col span={3} key={action}>
                    <Button
                      variant="default"
                      aria-label="Delete"
                      fullWidth
                      onClick={async () => {
                        datasetFormDispatch({
                          type: "CURRENT-ACTION",
                          value: "DELETE",
                        });

                        try {
                          let payload = {
                            ...dataset,
                            entityId,
                            metaData: {
                              action: "delete",
                            },
                          };
                          await postDataset(payload);
                          // setError("Form", "Unable to Submit");
                          datasetFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "VIEW",
                          });

                          datasetFormDispatch({
                            type: "SET-SUCCESS",
                            value: "Dataset Deleted Successfully",
                          });
                        } catch (error: any) {
                          // console.log("post catch", error);
                          datasetFormDispatch({
                            type: "SET-ERROR",
                            value: error?.response?.data?.message || "Unable to Delete Dataset",
                          });
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </Grid.Col>
                );
              } else {
                return null;
              }
            })}
          </>
        </Grid>

        <Audit audit={audit} />
        {datasetFormState.successMessage && <Success message={datasetFormState.successMessage} onOk={onOk} />}
      </form>
    </DatasetCVMContext.Provider>
  );
}
